/**
 * Centralna konfiguracja Radia Adamowo.
 *
 * To jedyne miejsce, w którym ustawia się adres streamu i adresy API radia.
 * Wartości pochodzą ze zmiennych środowiskowych Vite (plik `.env`), więc
 * podłączenie prawdziwego serwera radiowego nie wymaga zmian w kodzie.
 */

import { NIGHT_TAG } from './radioLayout';
import type { RadioMode } from './radio.types';
import type { RotationRules } from './radioQueue';

const clean = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/** Adres streamu audio (Icecast/Shoutcast mp3/aac albo HLS `.m3u8`). `null` = brak streamu. */
export const RADIO_STREAM_URL: string | null = clean(import.meta.env.VITE_RADIO_STREAM_URL);

/** Tryb wynika wyłącznie z konfiguracji: jest adres streamu → `stream`, nie ma → `demo`. */
export function resolveRadioMode(streamUrl: string | null | undefined): RadioMode {
  return streamUrl?.trim() ? 'stream' : 'demo';
}

export const RADIO_MODE: RadioMode = resolveRadioMode(RADIO_STREAM_URL);

/** Endpoint „teraz gra", np. `/api/radio/now-playing.php`. Używany tylko w trybie stream. */
export const RADIO_NOW_PLAYING_URL: string | null = clean(
  import.meta.env.VITE_RADIO_NOW_PLAYING_URL
);

/** Endpoint historii ostatnio granych pozycji. `null` = historia sesji. */
export const RADIO_HISTORY_URL: string | null = clean(import.meta.env.VITE_RADIO_HISTORY_URL);

/** Jak często odpytywać endpoint „teraz gra". */
export const RADIO_NOW_PLAYING_POLL_MS = 15_000;

export const RADIO_STATION = {
  name: 'Radio Adamowo',
  slogan: 'Nie przeszkadzamy.',
  tagline: 'Spokojnie gra.',
  description: 'Radio Adamowo – spokojna muzyka do słuchania w tle.',
  /** Okładka zastępcza (także dla systemowego odtwarzacza). */
  artwork: '/radio/icon.svg',
} as const;

/**
 * Reguły rotacji kolejki trybu demo (patrz `radioQueue.ts`).
 * Wstawki działają tylko wtedy, gdy w katalogu są pliki danego typu.
 */
export const RADIO_ROTATION_RULES: RotationRules = {
  vocalEvery: [5, 9],
  jingleEvery: [4, 8],
  interludeEveryMinutes: [30, 60],
  avoidRecent: 10,
  avoidSameArtist: true,
  // Muzyka z `music/night/`: w dzień pomijana, w nocy grana chętniej niż reszta.
  night: { tag: NIGHT_TAG, hours: [22, 6], boost: 4 },
};

export interface RadioProgram {
  /** Godzina rozpoczęcia pasma (włącznie), 0–23. */
  from: number;
  /** Godzina zakończenia pasma (wyłącznie), 1–24. */
  to: number;
  name: string;
}

/** Pasma dnia pokazywane w playerze, gdy API nie poda nazwy programu. */
export const RADIO_PROGRAMS: readonly RadioProgram[] = [
  { from: 0, to: 6, name: 'Noc' },
  { from: 6, to: 10, name: 'Poranek' },
  { from: 10, to: 17, name: 'W tle' },
  { from: 17, to: 21, name: 'Wieczór' },
  { from: 21, to: 24, name: 'Późna pora' },
];

export function getCurrentProgram(date: Date = new Date()): RadioProgram {
  const hour = date.getHours();
  return (
    RADIO_PROGRAMS.find((program) => hour >= program.from && hour < program.to) ?? RADIO_PROGRAMS[0]
  );
}
